<template>
  <div class="scheduler-manager p-6">
    <!-- Header Section -->
    <div class="header-section mb-6">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            Scheduler Manager
          </h1>
          <p class="text-gray-600">
            Monitor and control automated scheduling jobs across all services
          </p>
        </div>
        <div class="flex flex-col sm:flex-row gap-2">
          <Button label="Refresh Status" icon="pi pi-refresh"
           class="p-button-secondary" :loading="loading"
            @click="refreshStatus" />
          <Button label="Trigger All Jobs" icon="pi pi-play"
           class="p-button-primary" :loading="loading"
            @click="triggerAllJobs" />
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="statistics-section mb-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white p-4 rounded-lg shadow-sm border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Total Executions</p>
              <p class="text-2xl font-bold text-gray-800">
                {{ statistics.totalExecutions }}
              </p>
            </div>
            <i class="pi pi-chart-line text-blue-500 text-2xl"></i>
          </div>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Successful</p>
              <p class="text-2xl font-bold text-green-600">
                {{ statistics.successfulExecutions }}
              </p>
            </div>
            <i class="pi pi-check-circle text-green-500 text-2xl"></i>
          </div>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Failed</p>
              <p class="text-2xl font-bold text-red-600">
                {{ statistics.failedExecutions }}
              </p>
            </div>
            <i class="pi pi-times-circle text-red-500 text-2xl"></i>
          </div>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Scheduler Health</p>
              <p class="text-2xl font-bold" :class="healthColorClass">
                {{ schedulerHealthLabel }}
              </p>
            </div>
            <i :class="healthIconClass" class="text-2xl"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Job Status Cards -->
    <div class="job-status-section mb-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-800">Job Status</h2>
        <div class="text-sm text-gray-500">
          <i class="pi pi-info-circle mr-1"></i>
          Some jobs run automatically, others can be triggered manually
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          v-for="(job, jobType) in allJobs"
          :key="jobType"
          v-show="jobType !== 'hourly'"
          class="bg-white p-4 rounded-lg shadow-sm border"
          :class="getJobStatusClass(job.status)"
        >
          <div class="flex items-center justify-between mb-3">
            <h3 class="font-semibold text-gray-800 capitalize">{{ jobType }}</h3>
            <span
              class="px-2 py-1 text-xs font-medium rounded-full"
              :class="getStatusBadgeClass(job.status)"
            >
              {{ job.status }}
            </span>
          </div>

          <div class="space-y-2 text-sm">
            <div v-if="job.lastRun">
              <p class="text-gray-600">Last Run:</p>
              <p class="font-medium">{{ formatDate(job.lastRun) }}</p>
            </div>
            <div v-if="job.schemasProcessed > 0">
              <p class="text-gray-600">Schemas Processed:</p>
              <p class="font-medium text-blue-600">{{ job.schemasProcessed }}</p>
            </div>
          </div>

          <div class="mt-3">
            <Button
              :label="getTriggerButtonLabel(job.status)"
              :icon="getTriggerButtonIcon(job.status)"
              :class="getTriggerButtonClass(job.status)"
              size="small"
              :disabled="job.status === 'running'"
              @click="triggerJob(jobType)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Manual Controls Section -->
    <div class="manual-controls-section mb-6">
      <h2 class="text-xl font-semibold text-gray-800 mb-4">Manual Controls</h2>
      <div class="bg-white p-4 rounded-lg shadow-sm border">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Button label="Daily Jobs" icon="pi pi-calendar"
           class="p-button-outlined p-button-info" :loading="loading"
            @click="triggerJob('daily')" />
          <Button label="Weekly Jobs" icon="pi pi-calendar-plus"
           class="p-button-outlined p-button-success"
            :loading="loading" @click="triggerJob('weekly')" />
          <Button label="Monthly Jobs" icon="pi pi-calendar-times"
           class="p-button-outlined p-button-warning"
            :loading="loading" @click="triggerJob('monthly')" />
          <Button label="Yearly Jobs" icon="pi pi-calendar-minus"
           class="p-button-outlined p-button-danger"
            :loading="loading" @click="triggerJob('yearly')" />
          <Button label="Inactivity Reminder" icon="pi pi-bell"
           class="p-button-outlined p-button-help" :loading="loading"
            @click="runInactivityDetection" />
          <Button label="KSS Light Cron" icon="pi pi-clock"
           class="p-button-outlined p-button-info" :loading="loading"
            title="Runs KSS scheduled→open and mid-deadline (12h) TaskReminder"
            @click="runLightweightScheduling" />
          <Button label="Travel Buddy Matching" icon="pi pi-users"
           class="p-button-outlined p-button-secondary" :loading="loading"
            @click="triggerTravelBuddyMatching" />
          <Button label="Anomaly Report" icon="pi pi-exclamation-triangle"
           class="p-button-outlined p-button-danger" :loading="loading"
            @click="runAnomalyReport" />
        </div>
      </div>
    </div>

    <!-- Execution History Section -->
    <div class="execution-history-section mb-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-800">Execution History</h2>
        <div class="flex gap-2">
          <Button label="Clear History" icon="pi pi-trash"
           class="p-button-outlined p-button-danger" size="small"
            @click="clearHistory" />
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div v-if="executionHistory.length === 0" class="p-8 text-center text-gray-500">
          <i class="pi pi-history text-4xl mb-2"></i>
          <p>No execution history available</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium
                 text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium
                 text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium
                 text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium
                 text-gray-500 uppercase tracking-wider">
                  Message
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="execution in recentExecutions" :key="execution.id">
                <td class="px-4 py-3 whitespace-nowrap">
                  <span class="text-sm font-medium text-gray-900 capitalize">
                    {{ execution.type }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <span class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="getStatusBadgeClass(execution.status)">
                    {{ execution.status }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(execution.timestamp) }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-900">
                  {{ execution.message }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="error-section mb-6">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-center">
          <i class="pi pi-exclamation-triangle text-red-400 mr-2"></i>
          <div>
            <h3 class="text-sm font-medium text-red-800">Error</h3>
            <p class="text-sm text-red-700 mt-1">{{ error }}</p>
          </div>
          <Button icon="pi pi-times" class="p-button-text
           p-button-danger ml-auto" @click="clearError" />
        </div>
      </div>
    </div>

    <!-- Inactivity Report Dialog -->
    <Dialog
      v-model:visible="showInactivityReport"
      header="Inactivity Detection Report"
      :modal="true"
      :style="{ width: '80vw', maxWidth: '1000px' }"
      :dismissableMask="true"
    >
      <template v-if="inactivityReport">
        <div class="flex gap-4 mb-4 flex-wrap">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-1 text-center min-w-[100px]">
            <p class="text-sm text-gray-600">Total Found</p>
            <p class="text-2xl font-bold text-blue-700">
              {{ reportRows.length }}
            </p>
          </div>
          <div class="bg-green-50 border border-green-200 rounded-lg p-3 flex-1 text-center min-w-[100px]">
            <p class="text-sm text-gray-600">Sent</p>
            <p class="text-2xl font-bold text-green-700">
              {{ inactivityReport.totalNotified }}
            </p>
          </div>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 flex-1 text-center min-w-[100px]">
            <p class="text-sm text-gray-600">Skipped</p>
            <p class="text-2xl font-bold text-gray-600">
              {{ inactivityReport.totalSkipped }}
            </p>
          </div>
          <div v-if="inactivityReport.totalFailed > 0"
           class="bg-red-50 border border-red-200 rounded-lg p-3 flex-1 text-center min-w-[100px]">
            <p class="text-sm text-gray-600">Failed</p>
            <p class="text-2xl font-bold text-red-700">
              {{ inactivityReport.totalFailed }}
            </p>
          </div>
        </div>

        <div v-if="reportRows.length === 0" class="p-6 text-center text-gray-500">
          <i class="pi pi-check-circle text-4xl mb-2 text-green-400"></i>
          <p>No inactive users found. All users are on track!</p>
        </div>

        <DataTable
          v-else
          :value="reportRows"
          :paginator="reportRows.length > 10"
          :rows="10"
          stripedRows
          class="p-datatable-sm"
          sortField="status"
          :sortOrder="1"
        >
          <Column field="userName" header="User" sortable>
            <template #body="{ data }">
              <div>
                <span class="font-medium">{{ data.userName || data.userId }}</span>
                <span v-if="data.userEmail" class="block text-xs text-gray-500">
                  {{ data.userEmail }}
                </span>
              </div>
            </template>
          </Column>
          <Column field="level" header="Level" sortable style="width: 70px">
            <template #body="{ data }">
              <span class="font-semibold">{{ data.level }}</span>
            </template>
          </Column>
          <Column field="daysSinceLastProgress" header="Days Inactive" sortable
           style="width: 110px">
            <template #body="{ data }">
              <span class="px-2 py-1 text-xs font-medium rounded-full"
                :class="data.daysSinceLastProgress >= 30
                  ? 'bg-red-100 text-red-800'
                  : data.daysSinceLastProgress >= 14
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-yellow-100 text-yellow-800'">
                {{ data.daysSinceLastProgress }}d
              </span>
            </template>
          </Column>
          <Column field="lastReminderAt" header="Last Reminder" sortable
           style="width: 160px">
            <template #body="{ data }">
              <span v-if="data.lastReminderAt" class="text-sm text-gray-700">
                {{ formatDate(data.lastReminderAt) }}
              </span>
              <span v-else class="text-sm text-gray-400 italic">Never</span>
            </template>
          </Column>
          <Column field="reason" header="Reason" sortable style="width: 140px">
            <template #body="{ data }">
              <span class="px-2 py-1 text-xs font-medium rounded-full"
                :class="{
                  'bg-purple-100 text-purple-800': data.reason === 'Stabilisation',
                  'bg-orange-100 text-orange-800': data.reason === 'Inactivity',
                  'bg-gray-100 text-gray-700': data.reason === 'Cooldown active',
                }">
                {{ data.reason }}
              </span>
            </template>
          </Column>
          <Column field="status" header="Status" sortable style="width: 110px">
            <template #body="{ data }">
              <span v-if="data.status === 'sent'"
               class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                <i class="pi pi-check mr-1"></i>Sent
              </span>
              <span v-else-if="data.status === 'skipped'"
               class="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                <i class="pi pi-minus-circle mr-1"></i>Skipped
              </span>
              <span v-else
               class="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800"
               :title="data.error || ''">
                <i class="pi pi-times mr-1"></i>Failed
              </span>
            </template>
          </Column>
        </DataTable>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

export default {
  name: 'SchedulerManager',
  components: {
    Button,
    Dialog,
    DataTable,
    Column,
  },
  data() {
    return {
      showInactivityReport: false,
      inactivityReport: null,
    };
  },
  computed: {
    ...mapState('scheduler', [
      'loading',
      'error',
      'statistics',
      'executionHistory',
    ]),
    ...mapGetters('scheduler', [
      'allJobs',
      'schedulerHealth',
      'recentExecutions',
    ]),

    schedulerHealthLabel() {
      const health = this.schedulerHealth;
      switch (health) {
        case 'healthy': return 'Healthy';
        case 'warning': return 'Warning';
        case 'error': return 'Error';
        default: return 'Unknown';
      }
    },

    healthColorClass() {
      const health = this.schedulerHealth;
      switch (health) {
        case 'healthy': return 'text-green-600';
        case 'warning': return 'text-yellow-600';
        case 'error': return 'text-red-600';
        default: return 'text-gray-600';
      }
    },

    healthIconClass() {
      const health = this.schedulerHealth;
      switch (health) {
        case 'healthy': return 'pi pi-check-circle text-green-500';
        case 'warning': return 'pi pi-exclamation-triangle text-yellow-500';
        case 'error': return 'pi pi-times-circle text-red-500';
        default: return 'pi pi-question-circle text-gray-500';
      }
    },

    reportRows() {
      if (!this.inactivityReport) return [];
      return this.inactivityReport.rows || [];
    },
  },
  async mounted() {
    // Load initial status
    await this.getSchedulerStatus();
  },
  methods: {
    ...mapActions('scheduler', [
      'getSchedulerStatus',
      'triggerDevTipsScheduling',
      'triggerInactivityDetection',
      'triggerLightweightScheduling',
      'triggerTravelBuddyMatching',
      'triggerAnomalyReport',
      'triggerJob',
      'clearErrors',
      'resetSchedulerState',
    ]),

    async runInactivityDetection() {
      try {
        const result = await this.triggerInactivityDetection();
        if (result && result.report) {
          this.inactivityReport = result.report;
          this.showInactivityReport = true;
        }
      } catch (error) {
        console.error('Failed to trigger inactivity detection:', error);
      }
    },

    async runLightweightScheduling() {
      try {
        await this.triggerLightweightScheduling();
      } catch (error) {
        console.error('Failed to trigger lightweight scheduling:', error);
      }
    },

    async runAnomalyReport() {
      try {
        await this.triggerAnomalyReport();
      } catch (error) {
        console.error('Failed to trigger anomaly report:', error);
      }
    },

    async refreshStatus() {
      try {
        await this.getSchedulerStatus();
      } catch (error) {
        console.error('Failed to refresh status:', error);
      }
    },

    async triggerAllJobs() {
      try {
        await this.triggerDevTipsScheduling();
      } catch (error) {
        console.error('Failed to trigger all jobs:', error);
      }
    },

    async triggerSpecificJob(jobType) {
      try {
        await this.triggerJob(jobType);
      } catch (error) {
        console.error(`Failed to trigger ${jobType} job:`, error);
      }
    },

    clearError() {
      this.clearErrors();
    },

    clearHistory() {
      this.resetSchedulerState();
    },

    formatDate(dateString) {
      if (!dateString) return 'Never';
      const date = new Date(dateString);
      return date.toLocaleString();
    },

    getJobStatusClass(status) {
      switch (status) {
        case 'running': return 'border-blue-200 bg-blue-50';
        case 'completed': return 'border-green-200 bg-green-50';
        case 'failed': return 'border-red-200 bg-red-50';
        case 'idle': return 'border-gray-200 bg-gray-50';
        default: return 'border-gray-200';
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'success': return 'bg-green-100 text-green-800';
        case 'error': return 'bg-red-100 text-red-800';
        case 'running': return 'bg-blue-100 text-blue-800';
        case 'completed': return 'bg-green-100 text-green-800';
        case 'failed': return 'bg-red-100 text-red-800';
        case 'idle': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    },

    getTriggerButtonLabel(status) {
      switch (status) {
        case 'running': return 'Running...';
        case 'completed': return 'Run Again';
        case 'failed': return 'Retry';
        case 'idle': return 'Run Now';
        default: return 'Run';
      }
    },

    getTriggerButtonIcon(status) {
      switch (status) {
        case 'running': return 'pi pi-spinner pi-spin';
        case 'completed': return 'pi pi-refresh';
        case 'failed': return 'pi pi-replay';
        case 'idle': return 'pi pi-play';
        default: return 'pi pi-play';
      }
    },

    getTriggerButtonClass(status) {
      switch (status) {
        case 'running': return 'p-button-secondary p-button-loading';
        case 'completed': return 'p-button-outlined p-button-success';
        case 'failed': return 'p-button-outlined p-button-danger';
        case 'idle': return 'p-button-outlined p-button-primary';
        default: return 'p-button-outlined p-button-primary';
      }
    },
  },
};
</script>

<style scoped>
.scheduler-manager {
  max-width: 100%;
}

.job-status-section .grid>div {
  min-height: 160px;
}

.manual-controls-section .grid>div {
  min-height: 80px;
}

.execution-history-section table th {
  font-weight: 600;
}

.execution-history-section table td {
  vertical-align: top;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .statistics-section .grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .job-status-section .grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .manual-controls-section .grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .statistics-section .grid {
    grid-template-columns: 1fr;
  }

  .job-status-section .grid {
    grid-template-columns: 1fr;
  }
}
</style>
